import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { useRefresh } from '../RefreshContext';
import { useModalClose } from '../hooks/useModalClose';

function SplitCheck({ orderId, orderItems: initialOrderItems, totalPrice, tableName, orderName, theme, user, setShowSplitCheck, onClose, onSplitCompleted }) {
    const { t } = useLanguage();
    // eslint-disable-next-line no-unused-vars
    const { refreshKey, refresh } = useRefresh();
    useModalClose(onClose);
    const [orderItems, setOrderItems] = useState(initialOrderItems || []);
    const [selectedItems, setSelectedItems] = useState([]);
    const [splitName, setSplitName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [allOptions, setAllOptions] = useState([]);
    const [multipleMode, setMultipleMode] = useState(false);
    const [splitChecks, setSplitChecks] = useState([{ name: '', items: [] }]);
    // eslint-disable-next-line no-unused-vars
    const [draggedItem, setDraggedItem] = useState(null);
    const [existingOrderNames, setExistingOrderNames] = useState([]);
    const [nameValidation, setNameValidation] = useState({
        splitName: { isValid: true, message: '' },
        splitChecks: {}
    });

    // Fetch all options for mapping ids to names and existing order names
    useEffect(() => {
        refreshModalData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Local modal refresh function
    const refreshModalData = () => {
        // Fetch options
        fetch(`${BACKEND_URL}/api/debug/options`)
            .then(res => res.json())
            .then(data => setAllOptions(data))
            .catch(() => setAllOptions([]));

        // Fetch existing open order names (refresh when modal opens)
        refreshOrderNames();

        // Fetch order items for this order
        fetch(`${BACKEND_URL}/api/order-products?order_id=${orderId}`)
            .then(res => res.json())
            .then(data => setOrderItems(Array.isArray(data) ? data : []))
            .catch(() => setOrderItems([]));
    };

    const refreshOrderNames = () => {
        fetch(`${BACKEND_URL}/api/orders`)
            .then(res => res.json())
            .then(orders => {
                const names = orders
                    .filter(order => order.closed === 0) // Only open orders
                    .map(order => order.name.toLowerCase().trim()); // Normalize for comparison
                setExistingOrderNames(names);
            })
            .catch(() => setExistingOrderNames([]));
    };

    // Re-validate names when existing order names change
    useEffect(() => {
        if (existingOrderNames.length > 0) {
            // Re-validate single split name
            if (splitName.trim()) {
                const validation = validateSplitName(splitName);
                setNameValidation(prev => ({
                    ...prev,
                    splitName: validation
                }));
            }

            // Re-validate multiple split check names
            if (multipleMode) {
                const validationResults = validateSplitCheckNames();
                setNameValidation(prev => ({
                    ...prev,
                    splitChecks: validationResults
                }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingOrderNames, splitName, splitChecks, multipleMode]);

    // Helper to get option names from item.options (JSON string or array)
    const getOptionNames = (item, allOptions) => {
        if (!item.options) return null;
        let optionIds = [];
        if (typeof item.options === 'string') {
            try {
                optionIds = JSON.parse(item.options);
            } catch {
                return null;
            }
        } else if (Array.isArray(item.options)) {
            optionIds = item.options;
        }
        if (!Array.isArray(optionIds) || optionIds.length === 0) return null;
        if (!allOptions) return optionIds;
        // Filter out options with selected === 1
        return optionIds
            .map(id => allOptions.find(o => o.id === id))
            .filter(opt => opt && opt.selected !== 1)
            .map(opt => opt.name);
    };

    // Calculate selected items total
    const selectedTotal = selectedItems.reduce((sum, itemId) => {
        const item = orderItems.find(i => i.id === itemId);
        if (!item) return sum;
        const itemPrice = Number(item.price || 0) * item.quantity;
        const discount = Number(item.discount || 0);
        return sum + itemPrice - discount;
    }, 0);

    const handleItemToggle = (itemId) => {
        setSelectedItems(prev =>
            prev.includes(itemId)
                ? prev.filter(id => id !== itemId)
                : [...prev, itemId]
        );
    };

    const handleSelectAll = () => {
        if (selectedItems.length === orderItems.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(orderItems.map(item => item.id));
        }
    };

    // Functions for multiple split mode
    const addSplitCheck = () => {
        setSplitChecks([...splitChecks, { name: '', items: [] }]);
    };

    const removeSplitCheck = (index) => {
        if (splitChecks.length > 1) {
            const newSplitChecks = splitChecks.filter((_, i) => i !== index);
            setSplitChecks(newSplitChecks);
        }
    };

    const updateSplitCheckName = (index, name) => {
        const newSplitChecks = [...splitChecks];
        newSplitChecks[index].name = name;
        setSplitChecks(newSplitChecks);
    };

    const assignItemToSplitCheck = (itemId, splitCheckIndex) => {
        const newSplitChecks = [...splitChecks];

        // Remove item from all split checks first
        newSplitChecks.forEach(splitCheck => {
            splitCheck.items = splitCheck.items.filter(id => id !== itemId);
        });

        // Add item to selected split check
        if (splitCheckIndex >= 0) {
            newSplitChecks[splitCheckIndex].items.push(itemId);
        }

        setSplitChecks(newSplitChecks);
    };

    const getItemSplitCheck = (itemId) => {
        return splitChecks.findIndex(splitCheck => splitCheck.items.includes(itemId));
    };

    const getSplitCheckTotal = (splitCheck) => {
        return splitCheck.items.reduce((sum, itemId) => {
            const item = orderItems.find(i => i.id === itemId);
            if (!item) return sum;
            const itemPrice = Number(item.price || 0) * item.quantity;
            const discount = Number(item.discount || 0);
            return sum + itemPrice - discount;
        }, 0);
    };

    // Validation function to check for duplicate order names
    // eslint-disable-next-line no-unused-vars
    const validateOrderNames = (namesToCheck) => {
        const duplicates = namesToCheck.filter(name =>
            existingOrderNames.includes(name.toLowerCase().trim())
        );
        return duplicates;
    };

    // Real-time validation for single split name
    const validateSplitName = (name) => {
        if (!name.trim()) {
            return { isValid: true, message: '' }; // Empty is fine, will be caught at submit
        }

        const isDuplicate = existingOrderNames.includes(name.toLowerCase().trim());
        return {
            isValid: !isDuplicate,
            message: isDuplicate ? t('thisNameAlreadyExists') : ''
        };
    };

    // Real-time validation for multiple split check names
    const validateSplitCheckNames = () => {
        const validation = {};
        const allNames = splitChecks.map(sc => sc.name.toLowerCase().trim()).filter(name => name);

        splitChecks.forEach((splitCheck, index) => {
            const name = splitCheck.name.trim();
            if (!name) {
                validation[index] = { isValid: true, message: '' };
                return;
            }

            // Check against existing orders
            const isDuplicateExisting = existingOrderNames.includes(name.toLowerCase().trim());

            // Check against other split checks in this operation
            const nameCount = allNames.filter(n => n === name.toLowerCase()).length;
            const isDuplicateInternal = nameCount > 1;

            if (isDuplicateExisting) {
                validation[index] = { isValid: false, message: t('thisNameAlreadyExists') };
            } else if (isDuplicateInternal) {
                validation[index] = { isValid: false, message: t('duplicateNameInSplitChecks') };
            } else {
                validation[index] = { isValid: true, message: '' };
            }
        });

        return validation;
    };

    const handleSplitItem = async (itemId) => {
        const item = orderItems.find(i => i.id === itemId);
        if (!item || item.quantity <= 1) return;

        const confirmed = window.confirm(
            `${t('splitItem')} "${item.product_name}" x${item.quantity} ${t('intoSeparateItems')}?`
        );
        if (!confirmed) return;

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${BACKEND_URL}/api/order-products/split/${itemId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to split item');
            }

            // Refresh only the modal data
            refreshModalData();

        } catch (err) {
            console.error('Error splitting item:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle single split name change with validation
    const handleSplitNameChange = (newName) => {
        setSplitName(newName);
        const validation = validateSplitName(newName);
        setNameValidation(prev => ({
            ...prev,
            splitName: validation
        }));
    };

    // Handle multiple split check name change with validation
    const handleSplitCheckNameChange = (index, newName) => {
        updateSplitCheckName(index, newName);

        // Validate all split check names after this change
        setTimeout(() => {
            const validationResults = validateSplitCheckNames();
            setNameValidation(prev => ({
                ...prev,
                splitChecks: validationResults
            }));
        }, 0);
    };

    const handleSplitCheck = async () => {
        if (multipleMode) {
            return handleMultipleSplitCheck();
        }

        if (selectedItems.length === 0) {
            setError(t('pleaseSelectAtLeastOneItemToSplit'));
            return;
        }

        if (!splitName.trim()) {
            setError(t('pleaseEnterANameForTheSplitCheck'));
            return;
        }


        setLoading(true);
        setError('');

        try {
            // First, get current order data to preserve table_id
            const getResponse = await fetch(`${BACKEND_URL}/api/orders/${orderId}`);
            if (!getResponse.ok) {
                throw new Error(t('error') + ': Failed to fetch current order data');
            }
            const currentOrder = await getResponse.json();

            // Create a new order for the split
            const createOrderResponse = await fetch(`${BACKEND_URL}/api/orders`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    OrderName: splitName.trim(),
                    tableId: currentOrder.table_id, // Keep the same table as parent
                    user_id: user?.user_id || user?.id || 1,
                    parent_tab_id: orderId // Reference to parent order
                }),
            });

            if (!createOrderResponse.ok) {
                throw new Error(t('error') + ': Failed to create split order');
            }

            const newOrder = await createOrderResponse.json();
            const newOrderId = newOrder.id;

            // Move selected items to the new order
            for (const itemId of selectedItems) {
                const response = await fetch(`${BACKEND_URL}/api/order-products/${itemId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        order_id: newOrderId
                    }),
                });

                if (!response.ok) {
                    throw new Error(t('error') + `: Failed to move item ${itemId} to split check`);
                }
            }

            console.log('Check split successfully');
            onSplitCompleted();
        } catch (err) {
            console.error('Error splitting check:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleMultipleSplitCheck = async () => {
        // Validate all split checks have names and items
        const validSplitChecks = splitChecks.filter(sc => sc.name.trim() && sc.items.length > 0);

        if (validSplitChecks.length === 0) {
            setError(t('pleaseCreateAtLeastOneSplitCheckWithANameAndItems'));
            return;
        }

        // Check for empty names
        const emptyNames = splitChecks.filter(sc => sc.items.length > 0 && !sc.name.trim());
        if (emptyNames.length > 0) {
            setError(t('pleaseProvideNamesForAllSplitChecksThatHaveItems'));
            return;
        }


        setLoading(true);
        setError('');

        try {
            // First, get current order data to preserve table_id
            const getResponse = await fetch(`${BACKEND_URL}/api/orders/${orderId}`);
            if (!getResponse.ok) {
                throw new Error(t('error') + ': Failed to fetch current order data');
            }
            const currentOrder = await getResponse.json();

            // Create orders for each split check
            for (const splitCheck of validSplitChecks) {
                // Create a new order for this split
                const createOrderResponse = await fetch(`${BACKEND_URL}/api/orders`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        OrderName: splitCheck.name.trim(),
                        tableId: currentOrder.table_id,
                        user_id: user?.user_id || user?.id || 1,
                        parent_tab_id: orderId
                    }),
                });

                if (!createOrderResponse.ok) {
                    throw new Error(t('error') + `: Failed to create split order: ${splitCheck.name}`);
                }

                const newOrder = await createOrderResponse.json();
                const newOrderId = newOrder.id;

                // Move assigned items to this split check
                for (const itemId of splitCheck.items) {
                    const response = await fetch(`${BACKEND_URL}/api/order-products/${itemId}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            order_id: newOrderId
                        }),
                    });

                    if (!response.ok) {
                        throw new Error(t('error') + `: Failed to move item ${itemId} to split check ${splitCheck.name}`);
                    }
                }
            }

            console.log('Multiple checks split successfully');
            onSplitCompleted();
        } catch (err) {
            console.error('Error splitting into multiple checks:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                backgroundColor: theme?.colors?.surface || '#fff',
                borderRadius: theme?.borderRadius || 8,
                padding: '2rem',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
                <h2 style={{
                    color: theme?.colors?.primary || '#1976d2',
                    marginTop: 0,
                    textAlign: 'center'
                }}>
                    {t('splitCheck')}
                </h2>

                {/* Order Summary */}
                <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    backgroundColor: theme?.colors?.background || '#f5f5f5',
                    borderRadius: theme?.borderRadius || 6,
                    border: '1px solid #ddd'
                }}>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: theme?.colors?.text }}>{t('originalCheck')}</h3>
                    <div style={{ fontSize: '0.9rem', color: theme?.colors?.textSecondary }}>
                        {tableName && <div>{t('tableName')}: <strong>{tableName}</strong></div>}
                        {orderName && <div>{t('name')}: <strong>{orderName}</strong></div>}
                        <div>{t('total')} {t('items')}: <strong>{orderItems.length}</strong></div>
                        <div>{t('total')} {t('amount')}: <strong>${totalPrice.toFixed(2)}</strong></div>
                    </div>
                </div>

                {/* Split Mode Toggle */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        cursor: 'pointer',
                        color: theme?.colors?.text
                    }}>
                        <input
                            type="checkbox"
                            checked={multipleMode}
                            onChange={(e) => setMultipleMode(e.target.checked)}
                            style={{ transform: 'scale(1.2)' }}
                        />
                        <span style={{ fontWeight: 'bold' }}>{t('splitIntoMultipleChecks')}</span>
                    </label>
                </div>

                {!multipleMode ? (
                    /* Single Split Check Name */
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: 'bold',
                            color: theme?.colors?.text
                        }}>
                            {t('nameForSplitCheck')}:
                        </label>
                        <input
                            type="text"
                            value={splitName}
                            onChange={(e) => handleSplitNameChange(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: theme?.borderRadius || 4,
                                border: `1px solid ${!nameValidation.splitName.isValid ? '#d32f2f' : '#ddd'}`,
                                fontSize: '1rem',
                                backgroundColor: !nameValidation.splitName.isValid ? '#ffebee' : '#fff'
                            }}
                            placeholder={t('enterNameForTheSplitCheck')}
                        />
                        {!nameValidation.splitName.isValid && (
                            <div style={{
                                fontSize: '0.8rem',
                                color: '#d32f2f',
                                marginTop: '0.25rem'
                            }}>
                                {nameValidation.splitName.message}
                            </div>
                        )}
                    </div>
                ) : (
                    /* Multiple Split Checks */
                    <div style={{ marginBottom: '1.5rem' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '0.5rem'
                        }}>
                            <label style={{
                                fontWeight: 'bold',
                                color: theme?.colors?.text
                            }}>
                                {t('splitChecks')}:
                            </label>
                            <button
                                onClick={addSplitCheck}
                                style={{
                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    padding: '0.25rem 0.5rem',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem'
                                }}
                            >
                                + {t('addCheck')}
                            </button>
                        </div>

                        {splitChecks.map((splitCheck, index) => (
                            <div
                                key={index}
                                style={{
                                    border: '1px solid #ddd',
                                    borderRadius: theme?.borderRadius || 4,
                                    padding: '1rem',
                                    marginBottom: '0.5rem',
                                    backgroundColor: theme?.colors?.background || '#f5f5f5'
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '0.5rem'
                                }}>
                                    <div style={{ flex: 1, marginRight: '0.5rem' }}>
                                        <input
                                            type="text"
                                            value={splitCheck.name}
                                            onChange={(e) => handleSplitCheckNameChange(index, e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem',
                                                borderRadius: theme?.borderRadius || 4,
                                                border: `1px solid ${nameValidation.splitChecks[index] && !nameValidation.splitChecks[index].isValid ? '#d32f2f' : '#ddd'}`,
                                                fontSize: '0.9rem',
                                                backgroundColor: nameValidation.splitChecks[index] && !nameValidation.splitChecks[index].isValid ? '#ffebee' : '#fff'
                                            }}
                                            placeholder={`${t('splitCheck')} ${index + 1} ${t('name').toLowerCase()}...`}
                                        />
                                        {nameValidation.splitChecks[index] && !nameValidation.splitChecks[index].isValid && (
                                            <div style={{
                                                fontSize: '0.7rem',
                                                color: '#d32f2f',
                                                marginTop: '0.25rem'
                                            }}>
                                                {nameValidation.splitChecks[index].message}
                                            </div>
                                        )}
                                    </div>
                                    {splitChecks.length > 1 && (
                                        <button
                                            onClick={() => removeSplitCheck(index)}
                                            style={{
                                                backgroundColor: '#d32f2f',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: theme?.borderRadius || 4,
                                                padding: '0.25rem 0.5rem',
                                                cursor: 'pointer',
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            {t('remove')}
                                        </button>
                                    )}
                                </div>
                                <div style={{
                                    fontSize: '0.8rem',
                                    color: theme?.colors?.textSecondary,
                                    marginBottom: '0.25rem'
                                }}>
                                    {t('items')}: {splitCheck.items.length} | {t('total')}: ${getSplitCheckTotal(splitCheck).toFixed(2)}
                                </div>
                                <div style={{
                                    fontSize: '0.8rem',
                                    color: theme?.colors?.textSecondary
                                }}>
                                    {splitCheck.items.length > 0 ? (
                                        splitCheck.items.map(itemId => {
                                            const item = orderItems.find(i => i.id === itemId);
                                            return item ? item.product_name : '';
                                        }).join(', ')
                                    ) : (
                                        t('noItemsAssigned')
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Item Selection */}
                <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem'
                    }}>
                        <label style={{
                            fontWeight: 'bold',
                            color: theme?.colors?.text
                        }}>
                            {multipleMode ? t('assignItemsToSplitChecks') : t('selectItemsToSplit')}
                        </label>
                        {!multipleMode && (
                            <button
                                onClick={handleSelectAll}
                                style={{
                                    backgroundColor: 'transparent',
                                    color: theme?.colors?.primary || '#1976d2',
                                    border: `1px solid ${theme?.colors?.primary || '#1976d2'}`,
                                    borderRadius: theme?.borderRadius || 4,
                                    padding: '0.25rem 0.5rem',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem'
                                }}
                            >
                                {selectedItems.length === orderItems.length ? t('deselectAll') : t('selectAll')}
                            </button>
                        )}
                    </div>

                    <div style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: theme?.borderRadius || 4,
                        padding: '0.5rem'
                    }}>
                        {orderItems.map(item => {
                            const itemPrice = Number(item.price || 0) * item.quantity;
                            const discount = Number(item.discount || 0);
                            const finalPrice = itemPrice - discount;
                            const isSelected = selectedItems.includes(item.id);
                            const assignedSplitCheck = multipleMode ? getItemSplitCheck(item.id) : -1;

                            return (
                                <div
                                    key={item.id}
                                    onClick={multipleMode ? undefined : () => handleItemToggle(item.id)}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '0.5rem',
                                        margin: '0.25rem 0',
                                        backgroundColor: multipleMode ?
                                            (assignedSplitCheck >= 0 ? `${theme?.colors?.primary || '#1976d2'}20` : theme?.colors?.background || '#f5f5f5') :
                                            (isSelected ? `${theme?.colors?.primary || '#1976d2'}20` : theme?.colors?.background || '#f5f5f5'),
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: multipleMode ? 'default' : 'pointer',
                                        border: multipleMode ?
                                            (assignedSplitCheck >= 0 ? `2px solid ${theme?.colors?.primary || '#1976d2'}` : '1px solid #ddd') :
                                            (isSelected ? `2px solid ${theme?.colors?.primary || '#1976d2'}` : '1px solid #ddd')
                                    }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 'bold', color: theme?.colors?.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span>{item.product_name} x{item.quantity}</span>
                                            {item.quantity > 1 && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSplitItem(item.id);
                                                    }}
                                                    style={{
                                                        backgroundColor: theme?.colors?.secondary || '#666',
                                                        color: theme?.colors?.surface || '#fff',
                                                        border: 'none',
                                                        borderRadius: theme?.borderRadius || 4,
                                                        padding: '0.25rem 0.5rem',
                                                        cursor: 'pointer',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 'bold'
                                                    }}
                                                    title={t('splitItem')}
                                                >
                                                    {t('split')}
                                                </button>
                                            )}
                                        </div>
                                        {getOptionNames(item, allOptions) && getOptionNames(item, allOptions).length > 0 && (
                                            <div style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary }}>
                                                {getOptionNames(item, allOptions).join(', ')}
                                            </div>
                                        )}
                                        {item.note && (
                                            <div style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary, fontStyle: 'italic' }}>
                                                {t('note')}: {item.note}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{
                                        fontWeight: 'bold',
                                        color: theme?.colors?.text,
                                        marginLeft: '1rem'
                                    }}>
                                        ${finalPrice.toFixed(2)}
                                    </div>
                                    {multipleMode ? (
                                        <select
                                            value={assignedSplitCheck >= 0 ? assignedSplitCheck : ''}
                                            onChange={(e) => assignItemToSplitCheck(item.id, e.target.value === '' ? -1 : parseInt(e.target.value))}
                                            style={{
                                                marginLeft: '0.5rem',
                                                padding: '0.25rem',
                                                borderRadius: theme?.borderRadius || 4,
                                                border: '1px solid #ddd',
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            <option value="">{t('unassigned')}</option>
                                            {splitChecks.map((splitCheck, index) => (
                                                <option key={index} value={index}>
                                                    {splitCheck.name || `${t('check')} ${index + 1}`}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <div style={{
                                            width: '20px',
                                            height: '20px',
                                            borderRadius: '50%',
                                            border: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
                                            marginLeft: '0.5rem',
                                            backgroundColor: isSelected ? theme?.colors?.primary || '#1976d2' : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            {isSelected && (
                                                <div style={{
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#fff'
                                                }} />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Selected Total */}
                {!multipleMode && selectedItems.length > 0 && (
                    <div style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: theme?.colors?.primary || '#1976d2',
                        color: '#fff',
                        borderRadius: theme?.borderRadius || 6,
                        textAlign: 'center'
                    }}>
                        <h3 style={{ margin: '0', fontSize: '1.2rem' }}>
                            {t('splitCheck')} {t('total')}: ${selectedTotal.toFixed(2)}
                        </h3>
                        <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                            ({selectedItems.length} {selectedItems.length !== 1 ? t('items') : t('item')} {t('selected')})
                        </div>
                    </div>
                )}

                {/* Multiple Split Totals */}
                {multipleMode && splitChecks.some(sc => sc.items.length > 0) && (
                    <div style={{
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: theme?.colors?.primary || '#1976d2',
                        color: '#fff',
                        borderRadius: theme?.borderRadius || 6,
                        textAlign: 'center'
                    }}>
                        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem' }}>
                            {t('splitCheck')} {t('totals')}
                        </h3>
                        {splitChecks.map((splitCheck, index) => {
                            if (splitCheck.items.length === 0) return null;
                            const splitTotal = getSplitCheckTotal(splitCheck);
                            return (
                                <div key={index} style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                                    {splitCheck.name || `${t('check')} ${index + 1}`}: ${splitTotal.toFixed(2)} ({splitCheck.items.length} {splitCheck.items.length !== 1 ? t('items') : t('item')})
                                </div>
                            );
                        })}
                        <div style={{ fontSize: '1rem', marginTop: '0.5rem', fontWeight: 'bold' }}>
                            {t('total')} {t('split')}: ${splitChecks.reduce((sum, sc) => sum + getSplitCheckTotal(sc), 0).toFixed(2)}
                        </div>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div style={{
                        marginBottom: '1rem',
                        padding: '0.5rem',
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        borderRadius: theme?.borderRadius || 4,
                        fontSize: '0.9rem',
                        textAlign: 'center'
                    }}>
                        {error}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{
                            backgroundColor: theme?.colors?.secondary || '#666',
                            color: theme?.colors?.surface || '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.75rem 1.5rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        onClick={handleSplitCheck}
                        disabled={loading ||
                            (multipleMode ?
                                (splitChecks.every(sc => sc.items.length === 0) ||
                                 Object.values(nameValidation.splitChecks).some(v => !v.isValid)) :
                                (selectedItems.length === 0 || !nameValidation.splitName.isValid)
                            )}
                        style={{
                            backgroundColor: (loading ||
                                (multipleMode ?
                                    (splitChecks.every(sc => sc.items.length === 0) ||
                                     Object.values(nameValidation.splitChecks).some(v => !v.isValid)) :
                                    (selectedItems.length === 0 || !nameValidation.splitName.isValid)
                                )) ? '#ccc' : theme?.colors?.primary || '#1976d2',
                            color: '#fff',
                            border: 'none',
                            borderRadius: theme?.borderRadius || 4,
                            padding: '0.75rem 1.5rem',
                            cursor: (loading ||
                                (multipleMode ?
                                    (splitChecks.every(sc => sc.items.length === 0) ||
                                     Object.values(nameValidation.splitChecks).some(v => !v.isValid)) :
                                    (selectedItems.length === 0 || !nameValidation.splitName.isValid)
                                )) ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                        }}
                    >
                        {loading ?
                            (multipleMode ? t('creatingSplitChecks') : t('splitting')) :
                            (multipleMode ? t('createSplitChecks') : t('splitCheck'))
                        }
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SplitCheck;